import { useState } from 'react'
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  Loader2,
  XCircle,
} from 'lucide-react'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@deepguide-ai/ui/collapsible'
import { cn } from '@/lib/utils'

export const FINDINGS_DISPLAY_LIMIT = 40
export const POLL_INTERVAL_MS = 2000

export interface ScanSummaryShape {
  passedChecks: number
  reviewChecks: number
  failedChecks: number
  totalChecks: number
  plannedTotal: number | null
}

export interface ScanFinding {
  id: string
  level: 'pass' | 'warn' | 'fail' | 'info' | 'progress'
  message: string
  checkId?: string
  metadata?: Record<string, unknown>
  enrichment?: Array<{ controlDefId: string; controlTitle: string }>
}

export function ScanSummary({
  summary,
  controlsCovered,
  findings,
  phase,
  failureHint,
  renderFindingExtra,
}: {
  summary: ScanSummaryShape
  controlsCovered: number
  findings: ScanFinding[]
  phase: string
  /** Copy shown when the scan itself errored (not per-check failures). */
  failureHint: string
  /** Optional trailing content per finding — e.g. cloud enrichment pills. */
  renderFindingExtra?: (f: ScanFinding) => React.ReactNode
}) {
  const scanFailed = phase === 'failed'
  const visibleFindings = findings.filter(
    (f) => f.level === 'pass' || f.level === 'warn' || f.level === 'fail',
  )

  return (
    <div className="space-y-3">
      {/* Chip counts already live in the collapsed trigger — don't repeat them here. */}
      {(summary.plannedTotal != null || controlsCovered > 0) && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-body text-[11px] text-muted-foreground">
            {summary.plannedTotal != null
              ? `${summary.totalChecks} of ${summary.plannedTotal} checks run`
              : `${summary.totalChecks} checks run`}
          </span>
          {controlsCovered > 0 && (
            <span className="ml-auto inline-flex items-center rounded-full bg-primary/10 px-2.5 py-0.5 font-heading text-[11px] font-medium text-primary">
              Evidence for {controlsCovered} control
              {controlsCovered === 1 ? '' : 's'}
            </span>
          )}
        </div>
      )}

      {scanFailed && (
        <div className="rounded-sm border border-status-red/30 bg-status-red-bg px-3 py-2 font-body text-[12px] text-status-red-strong">
          {failureHint}
        </div>
      )}

      {visibleFindings.length > 0 && (
        <Collapsible>
          <CollapsibleTrigger className="group inline-flex items-center gap-1 font-body text-[11px] text-muted-foreground hover:text-foreground">
            <ChevronDown className="h-3 w-3 transition-transform group-data-[state=open]:rotate-180" />
            Show {visibleFindings.length} finding
            {visibleFindings.length === 1 ? '' : 's'}
          </CollapsibleTrigger>
          <CollapsibleContent>
            <ul className="mt-2 space-y-1.5">
              {visibleFindings.slice(0, FINDINGS_DISPLAY_LIMIT).map((f) => (
                <li
                  key={f.id}
                  className="flex items-start gap-2 rounded-sm bg-surface-subtle px-2.5 py-1.5"
                >
                  <FindingLevelIcon level={f.level} />
                  <div className="min-w-0 flex-1">
                    <div className="font-body text-[12px] text-foreground">
                      {f.message}
                    </div>
                    {renderFindingExtra?.(f)}
                  </div>
                </li>
              ))}
            </ul>
            {visibleFindings.length > FINDINGS_DISPLAY_LIMIT && (
              <p className="mt-2 font-body text-[11px] text-muted-foreground">
                Showing first {FINDINGS_DISPLAY_LIMIT}. Open Integrations
                for the full report.
              </p>
            )}
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  )
}

export function SummaryChip({
  icon,
  tone,
  label,
}: {
  icon: React.ReactNode
  tone: 'pass' | 'warn' | 'fail'
  label: string
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-heading text-[11px] font-medium',
        tone === 'pass' && 'bg-emerald-50 text-emerald-700',
        tone === 'warn' && 'bg-amber-50 text-amber-700',
        tone === 'fail' && 'bg-red-50 text-status-red-emphasis',
      )}
    >
      {icon}
      {label}
    </span>
  )
}

export function ScanLiveTail({ findings }: { findings: ScanFinding[] }) {
  // Progress-level events carry the per-check progress messages we
  // want to tail; filter to those so we don't mix in setup/result events.
  const tail = findings
    .filter(
      (f) =>
        f.level === 'progress' &&
        typeof (f.metadata?.lastRawCheckId ?? null) === 'string',
    )
    .slice(-6)
    .reverse()
  if (tail.length === 0) return null
  return (
    <ul className="mt-3 space-y-1 font-mono text-[11px] text-muted-foreground">
      {tail.map((f) => (
        <li key={f.id} className="truncate">
          {f.message}
        </li>
      ))}
    </ul>
  )
}

export function FindingLevelIcon({
  level,
}: {
  level: ScanFinding['level']
}) {
  if (level === 'pass')
    return (
      <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
    )
  if (level === 'warn')
    return (
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-status-orange" />
    )
  if (level === 'fail')
    return <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-status-red" />
  return (
    <div className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground" />
  )
}

/**
 * De-emphasized scan details: a single-line compact summary with a
 * disclosure chevron, expandable to the full progress + findings panel.
 *
 * Design rationale: on the onboarding Connect stage the goal is
 * *connection*, not audit. First-run scan findings create false
 * urgency ("34 failed!") when the user just needs to get tools
 * connected. This wrapper keeps the scan running + the evidence
 * flowing, but hides failure chatter until the user asks for it.
 */
export function ScanDetailsSection({
  isScanning,
  isTerminal,
  phase,
  percent,
  scannedCount,
  plannedTotal,
  summary,
  findings,
  controlsCovered,
  failureHint,
  renderFindingExtra,
  defaultOpen = false,
}: {
  isScanning: boolean
  isTerminal: boolean
  phase: string
  percent: number
  scannedCount: number
  plannedTotal: number | null
  summary: ScanSummaryShape | undefined
  findings: ScanFinding[]
  controlsCovered: number
  failureHint: string
  renderFindingExtra?: (f: ScanFinding) => React.ReactNode
  defaultOpen?: boolean
}) {
  const [open, setOpen] = useState(defaultOpen)
  const scanFailed = phase === 'failed'

  // Compact one-liner shown collapsed: "Scanning 5 of 20" while running,
  // "12 passed · 3 review · 5 failed" after complete.
  const compact = isScanning ? (
    <span className="inline-flex items-center gap-1.5 font-body text-[12px] text-primary">
      <Loader2 className="h-3.5 w-3.5 animate-spin" />
      {plannedTotal != null
        ? `Scanning ${scannedCount} of ${plannedTotal}`
        : `Scanning… ${percent}%`}
    </span>
  ) : isTerminal && summary ? (
    <span className="inline-flex items-center gap-3 font-body text-[12px] text-muted-foreground">
      <span className="inline-flex items-center gap-1">
        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
        {summary.passedChecks} passed
      </span>
      {summary.reviewChecks > 0 && (
        <span className="inline-flex items-center gap-1">
          <AlertTriangle className="h-3.5 w-3.5 text-status-orange" />
          {summary.reviewChecks} review
        </span>
      )}
      {summary.failedChecks > 0 && (
        <span className="inline-flex items-center gap-1">
          <XCircle className="h-3.5 w-3.5 text-status-red" />
          {summary.failedChecks} failed
        </span>
      )}
      {scanFailed && (
        <span className="font-medium text-amber-700">Scan error</span>
      )}
    </span>
  ) : null

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="group flex w-full items-center justify-between gap-2 px-4 py-2.5 text-left transition-colors hover:bg-surface-subtle">
        <div className="min-w-0 flex-1">{compact}</div>
        <span className="inline-flex shrink-0 items-center gap-1 font-body text-[11px] text-muted-foreground">
          {open ? 'Hide details' : 'View details'}
          <ChevronDown className="h-3 w-3 transition-transform group-data-[state=open]:rotate-180" />
        </span>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="px-4 pb-3">
          {isScanning && (
            <>
              <ScanProgressBar
                scannedCount={scannedCount}
                plannedTotal={plannedTotal}
                percent={percent}
              />
              <ScanLiveTail findings={findings} />
            </>
          )}
          {!isScanning && isTerminal && summary && (
            <ScanSummary
              summary={summary}
              controlsCovered={controlsCovered}
              findings={findings}
              phase={phase}
              failureHint={failureHint}
              renderFindingExtra={renderFindingExtra}
            />
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}

export function ScanProgressBar({
  scannedCount,
  plannedTotal,
  percent,
}: {
  scannedCount: number
  plannedTotal: number | null
  percent: number
  /** Deprecated — kept for backwards compat; phase is shown in the header. */
  phase?: string
}) {
  const displayPercent =
    plannedTotal != null && plannedTotal > 0
      ? Math.min(100, Math.round((scannedCount / plannedTotal) * 100))
      : percent
  return (
    <div className="flex items-center gap-2">
      <div className="h-1 flex-1 rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-primary transition-[width] duration-300"
          style={{ width: `${displayPercent}%` }}
        />
      </div>
      <span className="shrink-0 font-body text-[11px] tabular-nums text-muted-foreground">
        {displayPercent}%
      </span>
    </div>
  )
}
